'use client';

import React from 'react';
import { Clock, TrendingUp, TrendingDown } from 'lucide-react';
import type { PaperTrade } from '../utils/paperTradingStorage';

interface TradeHistoryTableProps {
  trades: PaperTrade[];
}

const EXIT_REASON_LABELS: Record<PaperTrade['exitReason'], string> = {
  manual: 'Manual',
  stop_loss: 'Stop Loss',
  take_profit: 'Take Profit',
};

export default function TradeHistoryTable({ trades }: TradeHistoryTableProps) {
  if (trades.length === 0) {
    return (
      <div className="bg-card border border-border rounded-2xl p-6">
        <h2 className="text-sm font-700 text-foreground mb-4">Trade History</h2>
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <div className="w-12 h-12 rounded-2xl bg-muted/50 flex items-center justify-center mb-3">
            <Clock size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">No closed trades yet</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Close a position to see trade history</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-700 text-foreground">Trade History</h2>
        <span className="text-xs text-muted-foreground">{trades.length} trade{trades.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border">
              {['Symbol', 'Dir', 'Qty', 'Entry', 'Exit', 'P&L', 'P&L %', 'Exit Reason', 'Strategy', 'Closed'].map((h) => (
                <th key={h} className="text-left text-muted-foreground font-500 pb-2.5 pr-3 whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border/50">
            {trades.map((trade) => {
              const isProfit = trade.pnl >= 0;
              const closedDate = new Date(trade.closedAt);
              const dateStr = `${closedDate.getMonth() + 1}/${closedDate.getDate()} ${closedDate.getHours()}:${String(closedDate.getMinutes()).padStart(2, '0')}`;

              return (
                <tr key={trade.id} className="hover:bg-muted/30 transition-colors">
                  <td className="py-2.5 pr-3 font-700 text-foreground">{trade.symbol}</td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`flex items-center gap-1 font-600 ${
                        trade.direction === 'long' ? 'text-emerald-400' : 'text-red-400'
                      }`}
                    >
                      {trade.direction === 'long' ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                      {trade.direction === 'long' ? 'L' : 'S'}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-foreground tabular-nums">{trade.quantity}</td>
                  <td className="py-2.5 pr-3 text-foreground tabular-nums">${trade.entryPrice.toFixed(2)}</td>
                  <td className="py-2.5 pr-3 text-foreground tabular-nums">${trade.exitPrice.toFixed(2)}</td>
                  <td className={`py-2.5 pr-3 font-700 tabular-nums ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : ''}${trade.pnl.toFixed(2)}
                  </td>
                  <td className={`py-2.5 pr-3 font-600 tabular-nums ${isProfit ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isProfit ? '+' : ''}{trade.pnlPct.toFixed(2)}%
                  </td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`px-2 py-0.5 rounded-full font-500 ${
                        trade.exitReason === 'take_profit' ?'bg-emerald-500/10 text-emerald-400'
                          : trade.exitReason === 'stop_loss' ?'bg-red-500/10 text-red-400' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      {EXIT_REASON_LABELS[trade.exitReason]}
                    </span>
                  </td>
                  <td className="py-2.5 pr-3 text-muted-foreground max-w-[120px] truncate">{trade.strategy}</td>
                  <td className="py-2.5 text-muted-foreground tabular-nums whitespace-nowrap">{dateStr}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
